import axios from "axios";

// Token storage. Wave-12 dev-auth flow: browser POSTs to /api/v1/dev/login
// (platform-security's DevLoginController, active under local/standalone),
// stores the HS256 JWT in localStorage, and the request interceptor below
// attaches it as a Bearer header. When datumcraft.security.enabled=false (the
// default in local/standalone) the server ignores the header anyway, so
// the same code path works whether security is gated or not.
const TOKEN_KEY = "atlas.auth.token";

export function setAuthToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

export const api = axios.create({
  baseURL: "/api/v1/atlas",
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Platform endpoints (license, health, config) live OUTSIDE the atlas
// namespace — shared with curate / operate. Separate axios instance so
// `api` callers don't have to think about the prefix.
export const platformApi = axios.create({
  baseURL: "/api/v1",
  withCredentials: true,
});

platformApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ----- Platform DTOs (mirror Java records in platform-licensing /
//       atlas-api's AtlasConfigController) ----------------------------

export type LicenseStatus = {
  ok: boolean;
  failureReason?: string | null;
  failureMessage?: string | null;
  customerName?: string | null;
  issuedAt?: string | null;
  expiresAt?: string | null;
  daysRemaining: number;
  fingerprint: string;
};

export type ConfigResponse = {
  llmProvider: string;
  productVersion: string;
  ruleCount: number;
  featureFlags: Record<string, boolean>;
};

export type RuleDescriptor = {
  id: string;
  className: string;
};

// ----- Domain types — mirror Java records in atlas-core -----

export type AssetId = { platform: string; value: string };
export type UserId = { platform: string; value: string };

export type Severity = "BLOCKER" | "WARN" | "INFO";

export type GovernedPlatform = "COGNOS" | "POWERBI" | "TABLEAU" | "QLIK" | "OTHER";

export type EntityKind =
  | "REPORT" | "DASHBOARD" | "DATASET" | "SEMANTIC_MODEL"
  | "PACKAGE" | "DATA_SOURCE" | "FOLDER" | "WORKBOOK" | "OTHER";

export type GovernedEntity = {
  id: AssetId;
  platform: GovernedPlatform;
  displayName: string;
  entityKind: EntityKind;
  folderPath: string;
  owner?: UserId | null;
  tags: string[];
  classifications: string[];
  createdAt?: string | null;
  lastAccessed?: string | null;
};

export type GovernanceFinding = {
  assetId: AssetId;
  ruleId: string;
  severity: Severity;
  title: string;
  details: string;
  fixHint: string;
  category: string;
};

export type ComplianceReport = {
  scanId: string;
  scope: string;
  generatedAt: string;
  entitiesScanned: number;
  entitiesAtRisk: number;
  findings: GovernanceFinding[];
  countsBySeverity: Partial<Record<Severity, number>>;
};

export type ScanMode = "scan" | "compliance" | "anomalies";

export async function runScan(
  mode: ScanMode,
  scope: string,
  entities: GovernedEntity[],
): Promise<ComplianceReport> {
  const r = await api.post<ComplianceReport>(`/governance/${mode}`, { scope, entities });
  return r.data;
}

// ----- Graph-driven scans -----
//
// Replace the inline-entities flow with a tenant + scope pair the
// server resolves against the unified metadata graph. The graph is
// populated by ingesters (see runIngestCognos below); this is the
// flow the SPA defaults to once an ingest has run.

const GRAPH_SCAN_PATH: Record<ScanMode, string> = {
  scan: "/governance/scan-from-graph",
  compliance: "/governance/compliance-from-graph",
  anomalies: "/governance/anomalies-filtered-from-graph",
};

export async function runScanFromGraph(
  mode: ScanMode,
  tenantId: string,
  scope: string,
): Promise<ComplianceReport> {
  const r = await api.post<ComplianceReport>(GRAPH_SCAN_PATH[mode], {
    tenantId, scope,
  });
  return r.data;
}

export async function fetchSummaryFromGraph(
  tenantId: string,
  scope: string,
): Promise<GovernanceImpactSummary> {
  const r = await api.post<GovernanceImpactSummary>(
    "/governance/summary-from-graph",
    { tenantId, scope },
  );
  return r.data;
}

export async function downloadReportCsvFromGraph(
  tenantId: string,
  scope: string,
): Promise<void> {
  const r = await api.post("/governance/report.csv-from-graph",
    { tenantId, scope }, { responseType: "blob" });
  triggerDownload(r, "atlas-compliance.csv", "text/csv");
}

export async function downloadReportPdfFromGraph(
  tenantId: string,
  scope: string,
): Promise<void> {
  const r = await api.post("/governance/report.pdf-from-graph",
    { tenantId, scope }, { responseType: "blob" });
  triggerDownload(r, "atlas-compliance.pdf", "application/pdf");
}

// ----- Ingest -----

export type IngestRunStatus = "RUNNING" | "SUCCESS" | "FAILED";

export type IngestRun = {
  runId: string;
  tenantId: string;
  source: string;
  rootPath: string;
  status: IngestRunStatus;
  startedAt: string;
  finishedAt: string | null;
  containersWalked: number;
  assetsIngested: number;
  usersIngested: number;
  skipped: number;
  truncated: boolean;
  errorMessage: string | null;
  warnings: string[];
};

export type IngestStatus = {
  current: IngestRun | null;
  last: IngestRun | null;
  sources: Record<string, boolean>;
};

export async function runIngestCognos(
  tenantId: string,
  rootPath: string | null,
  maxNodes: number | null,
): Promise<IngestRun> {
  const body: Record<string, unknown> = { tenantId };
  if (rootPath) body.rootPath = rootPath;
  if (maxNodes) body.maxNodes = maxNodes;
  const r = await api.post<IngestRun>("/ingest/cognos", body);
  return r.data;
}

export async function fetchIngestStatus(): Promise<IngestStatus> {
  const r = await api.get<IngestStatus>("/ingest/status");
  return r.data;
}

function triggerDownload(
  r: { data: unknown; headers: unknown },
  fallbackName: string,
  fallbackMime: string,
): void {
  const headers = (r.headers ?? {}) as Record<string, unknown>;
  const cdRaw = headers["content-disposition"];
  const cd = typeof cdRaw === "string" ? cdRaw : "";
  const filenameMatch = cd.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : fallbackName;
  const blob = r.data instanceof Blob
    ? r.data
    : new Blob([r.data as BlobPart], { type: fallbackMime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ----- Aggregate summary -----

export type TopOffender = {
  assetPlatform: string;
  assetId: string;
  totalFindings: number;
  blockerCount: number;
  warnCount: number;
};

export type GovernanceImpactSummary = {
  entitiesScanned: number;
  entitiesAtRisk: number;
  totalFindings: number;
  bySeverity: Partial<Record<Severity, number>>;
  byCategory: Record<string, number>;
  byRule: Record<string, number>;
  topOffenders: TopOffender[];
};

export async function fetchSummary(
  scope: string,
  entities: GovernedEntity[],
): Promise<GovernanceImpactSummary> {
  const r = await api.post<GovernanceImpactSummary>(
    "/governance/summary",
    { scope, entities },
  );
  return r.data;
}

/** Same browser-download pattern as PDF, but for CSV. */
export async function downloadReportCsv(
  scope: string,
  entities: GovernedEntity[],
): Promise<void> {
  const r = await api.post("/governance/report.csv", { scope, entities }, {
    responseType: "blob",
  });
  const cd = (r.headers["content-disposition"] as string | undefined) ?? "";
  const filenameMatch = cd.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : "atlas-compliance.csv";
  const blob = r.data instanceof Blob ? r.data : new Blob([r.data], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Render the current scope+entities as a PDF and trigger a browser
 * download. Bypasses the JSON axios instance so we get the raw bytes
 * + Content-Disposition header back.
 */
export async function downloadReportPdf(
  scope: string,
  entities: GovernedEntity[],
): Promise<void> {
  const r = await api.post("/governance/report.pdf", { scope, entities }, {
    responseType: "blob",
  });
  const cd = (r.headers["content-disposition"] as string | undefined) ?? "";
  const filenameMatch = cd.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : "atlas-compliance.pdf";
  const blob = r.data instanceof Blob ? r.data : new Blob([r.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
